import { useEffect, useState } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { json } from "@codemirror/lang-json";
import { ensureSyntaxTree, foldAll } from "@codemirror/language";
import { oneDark } from "@codemirror/theme-one-dark";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface EditModalProps {
  keyName: string;
  initialValue: string;
  onSave: (value: string) => void;
  onClose: () => void;
}

function prettify(s: string): string {
  if (!s) return "";
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}

export function EditModal({ keyName, initialValue, onSave, onClose }: EditModalProps) {
  const [text, setText] = useState(() => prettify(initialValue));
  const [error, setError] = useState("");

  // Esc closes the modal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  function handleSave() {
    try {
      const parsed = JSON.parse(text);
      // Always store minified to keep PlayFab's per-key size cost low.
      onSave(JSON.stringify(parsed));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Invalid JSON");
    }
  }

  function handleFormat() {
    setText(prettify(text));
    setError("");
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={(e) => {
        if (e.currentTarget === e.target) onClose();
      }}
    >
      <div className="w-full max-w-4xl rounded-lg border border-border bg-card flex flex-col max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h2 className="text-sm font-medium font-mono truncate">{keyName}</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground shrink-0"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="flex-1 overflow-auto">
          <CodeMirror
            value={text}
            onChange={(v) => {
              setText(v);
              if (error) setError("");
            }}
            theme={oneDark}
            extensions={[json()]}
            height="60vh"
            basicSetup={{
              lineNumbers: true,
              foldGutter: true,
              highlightActiveLine: true,
              bracketMatching: true,
              closeBrackets: true,
              indentOnInput: true,
              autocompletion: false,
            }}
            onCreateEditor={(view) => {
              // Force a full parse synchronously so deep nodes are known to be
              // foldable, then collapse everything. Without this, the JSON
              // parser is incremental and `foldAll` only sees the upper layers
              // — leaving the deepest (often largest) blob un-collapsible.
              ensureSyntaxTree(view.state, view.state.doc.length, 5000);
              foldAll(view);
            }}
          />
        </div>
        {error && (
          <div className="px-5 py-2 border-t border-border">
            <Alert variant="destructive">
              <AlertDescription className="text-xs">{error}</AlertDescription>
            </Alert>
          </div>
        )}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border">
          <Button variant="ghost" onClick={handleFormat}>
            Format
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save</Button>
        </div>
      </div>
    </div>
  );
}
